import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { getString } from "config-api";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import logger from "../../../../utils/logger/library";

const NewsletterForm = ({
    button_text,
    is_subscribed,
    name,
    newsletter,
    subscribe_info,
    subscribeAction,
    success_message,
    tried_subscribe,
}) => {
    const renderTimeRef = useRef(new Date().getTime());
    const previousNewsletterRef = useRef(newsletter);
    const emailTextInput = useRef();
    const recaptchaEnabled = useFeatureFlag("newsletters.recaptcha");
    const [state, setState] = useState({
        error: false,
        showLoading: false,
        showSuccess: is_subscribed ? "show" : "hide",
        disableForm: false,
        formClass: `formSubscribe nws_id_${newsletter}`,
        honeypot: "",
    });
    const handleSubmit = async (event) => {
        event.preventDefault();
        let recaptcha_token = null;
        if (recaptchaEnabled) {
            if (window.grecaptcha || window.grecaptcha?.ready) {
                recaptcha_token = await window.grecaptcha.execute(
                    getString("Client.newsletters.recaptchaPublicKey"),
                    { action: "subscribe" }
                );
            } else {
                logger.info("recaptcha not ready");
            }
        }
        setState((s) => ({
            ...s,
            error: false,
            showLoading: "show",
            showSuccess: false,
            disableForm: true,
        }));
        subscribeAction(
            newsletter,
            name,
            event.target.elements.email.value,
            event.target.elements.current_time.value,
            event.target.elements.business_number.value,
            recaptcha_token,
            "subscribe"
        );
    };

    useEffect(() => {
        if (recaptchaEnabled && !window.grecaptcha) {
            const scriptElement = document.createElement("script");
            scriptElement.src = `https://www.google.com/recaptcha/api.js?render=${getString(
                "Client.newsletters.recaptchaPublicKey"
            )}`;
            scriptElement.async = true;
            document.head.appendChild(scriptElement);
            return () => {};
        }
    }, []);

    useEffect(() => {
        const is_new_widget = previousNewsletterRef.current !== newsletter;
        if (is_new_widget && !is_subscribed) {
            setState((s) => ({
                ...s,
                error: false,
                showLoading: false,
                showSuccess: "hide",
                disableForm: false,
                formClass: `formSubscribe nws_id_${newsletter}`,
            }));
        } else if (tried_subscribe) {
            const { errors, message, result } = subscribe_info;
            if (
                (message || result) &&
                (!state.showSuccess || state.showSuccess === "hide")
            ) {
                setState((s) => ({
                    ...s,
                    error: false,
                    showLoading: false,
                    showSuccess: "show",
                }));
            } else if (errors && state.error !== errors.error) {
                setState((s) => ({
                    ...s,
                    error: errors.error,
                    showLoading: false,
                    showSuccess: false,
                    disableForm: false,
                }));
            }
        }
    }, [newsletter, subscribe_info, is_subscribed]);

    useEffect(() => {
        if (state.error) {
            emailTextInput.current.focus();
        }
    }, [state.error]);
    const onHoneypotChange = (event) => {
        setState((s) => ({
            ...s,
            honeypot: event.target.value,
        }));
    };
    const show_e = state.error ? "show" : "";
    const error_aria_invalid = state.error ? "true" : "false";
    const render_time = renderTimeRef.current;
    const hideForm = is_subscribed ? "hide" : "show";
    const recaptchaMessage = recaptchaEnabled ? (
        <div id="recaptcha-container-msg">
            <p id="recaptcha-privacy-msg" className="supplemental">
                This site is protected by reCAPTCHA and the Google{" "}
                <a href="https://policies.google.com/privacy">Privacy Policy</a>{" "}
                and Google{" "}
                <a href="https://policies.google.com/terms">Terms of Service</a>{" "}
                apply.
            </p>
        </div>
    ) : null;

    return (
        <form
            method="post"
            action=""
            className={state.formClass}
            onSubmit={handleSubmit}
        >
            <div className={hideForm}>
                <div className="input-wrapper">
                    <label htmlFor="emailInput" className="inputLabel">
                        Email address:
                    </label>
                    <div className="input-group">
                        <input
                            id="emailInput"
                            aria-describedby="emailError"
                            aria-invalid={error_aria_invalid}
                            type="email"
                            name="email"
                            className="textbox"
                            required
                            placeholder="Enter your email address"
                            ref={emailTextInput}
                            disabled={state.disableForm}
                            defaultValue=""
                        />
                        <div
                            aria-label="input error"
                            className={`error-icon ${show_e}`}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                            >
                                <path d="M0 0h24v24H0z" fill="none" />
                                <path
                                    fill="#E60606"
                                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                                />
                            </svg>
                        </div>
                    </div>
                    <div className={`submit-error ${show_e}`} id="emailError">
                        {state.error}
                    </div>
                </div>
                <div className="submit-wrapper">
                    <button
                        data-cy="newsletters-subscribe-button"
                        type="submit"
                        className="submit"
                        disabled={state.disableForm}
                    >
                        {button_text}
                    </button>
                </div>
                <div className="screenReader">
                    <label htmlFor={`current_time_${newsletter}`}>
                        Current Time
                    </label>
                    <input
                        type="hidden"
                        id={`current_time_${newsletter}`}
                        name="current_time"
                        value={render_time}
                    />
                    <label htmlFor={`business_number_${newsletter}`}>
                        Ignore this field. If any data is entered for this
                        field, you will not be subscribed to this newsletter.
                    </label>
                    <input
                        type="text"
                        id={`business_number_${newsletter}`}
                        name="business_number"
                        size="1"
                        tabIndex="-1"
                        value={state.honeypot}
                        onChange={onHoneypotChange}
                    />
                </div>
                <div className={`loading ${state.showLoading}`}>
                    <div>
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                    </div>
                </div>
            </div>
            <div
                className={classNames(
                    "newsletterSubmitSuccess",
                    state.showSuccess
                )}
            >
                <p>{success_message}</p>
            </div>
            {recaptchaMessage}
        </form>
    );
};

export default NewsletterForm;
